/**
 * Widget-specific normalization and default-shape helpers for the dashboard
 * data provider.
 */
const dashboardWidgetDataProviderWidgets = {
    /**
     * Get supported breakpoints index.
     */
    getSupportedBreakpointsIndex() {
        const index = {};
        const supportedBreakpoints = Array.isArray(this.supportedBreakpoints)
            ? this.supportedBreakpoints
            : [];

        for (const breakpoint of supportedBreakpoints) {
            if (!breakpoint?.id) {
                continue;
            }

            index[breakpoint.id] = breakpoint;
        }

        return index;
    },

    /**
     * Get widget type index.
     */
    getWidgetTypeIndex() {
        const index = {};
        const supportedWidgets = Array.isArray(this.supportedWidgets)
            ? this.supportedWidgets
            : [];

        for (const widgetType of supportedWidgets) {
            const resolvedType = this.normalizeWidgetType(widgetType?.type || widgetType?.id || '');

            if (!resolvedType) {
                continue;
            }

            index[resolvedType] = widgetType;
        }

        return index;
    },

    /**
     * Normalize widget type.
     *
     * @param {*} value
     */
    normalizeWidgetType(value) {
        if (value === null || typeof value === 'undefined') {
            return '';
        }

        const normalizedValue = String(value).trim();

        return normalizedValue === '' || normalizedValue === 'undefined'
            ? ''
            : normalizedValue;
    },

    /**
     * Clone settings.
     *
     * @param {*} values
     */
    cloneSettings(values = {}) {
        return JSON.parse(JSON.stringify(values));
    },

    /**
     * Get widget settings definition.
     *
     * @param {*} widgetType
     */
    getWidgetSettingsDefinition(widgetType) {
        const settings = widgetType?.settings && typeof widgetType.settings === 'object'
            ? this.cloneSettings(widgetType.settings)
            : {
                values: {},
                hasForm: false,
            };

        settings.values = settings.values && typeof settings.values === 'object'
            ? settings.values
            : {};

        return settings;
    },

    /**
     * Get widget default settings values.
     *
     * @param {*} widgetType
     */
    getWidgetDefaultSettingsValues(widgetType) {
        const defaultData = widgetType?.defaultData && typeof widgetType.defaultData === 'object'
            ? widgetType.defaultData
            : {};

        if (Object.keys(defaultData).length) {
            return this.cloneSettings(defaultData);
        }

        return this.cloneSettings(widgetType?.settings?.values || {});
    },

    /**
     * Get widget settings values.
     *
     * @param {*} widget
     */
    getWidgetSettingsValues(widget) {
        if (widget?.settings && typeof widget.settings === 'object' && widget.settings.values && typeof widget.settings.values === 'object') {
            return this.cloneSettings(widget.settings.values);
        }

        return this.cloneSettings(widget?.settings || {});
    },

    /**
     * Generate a unique client-side reference id for a new widget.
     */
    getNewReferenceId() {
        return Date.now().toString(36) + Math.random().toString(36).substr(2);
    },

    /**
     * Build the shared editor block shape for a new widget type.
     *
     * @param {*} type
     */
    createEditorBlockByType(type) {
        return {
            id: null,
            referenceId: this.getNewReferenceId(),
            name: type.name,
            nameDefault: type.nameDefault,
            canEditName: type.canEditName,
            type: type.type,
            minColspan: type.minColspan,
            colspan: 12,
            offset: 0,
            endOfRow: false,
            areChildrenAllowed: type.areChildrenAllowed,
            requiredParent: type.requiredParent,
            settings: type.settings,
            locks: type.locks,
            conditions: type.conditions,
            icon: type.icon,
            size: type.size,
            helpText: type.helpText,
            blocks: [],
        };
    },

    /**
     * Get new widget by type.
     *
     * @param {*} type
     */
    getNewWidgetByType(type) {
        const widget = this.createEditorBlockByType(type);
        widget.type = this.normalizeWidgetType(type.type || type.id || '');
        const defaultColumn = Math.max(Number(type.defaultColumn ?? 1), 1);
        const defaultRow = Math.max(Number(type.defaultRow ?? 1), 1);
        const defaultColumnSpan = Math.max(Number(type.defaultColumnSpan ?? 12), 1);
        const defaultRowSpan = Math.max(Number(type.defaultRowSpan ?? 1), 1);

        widget.settings = this.getWidgetSettingsDefinition(type);
        widget.settings.values = this.getWidgetDefaultSettingsValues(type);
        widget.name = type.name || '';
        widget.canEditName = type.canEditName ?? true;
        widget.defaultColumn = defaultColumn;
        widget.defaultRow = defaultRow;
        widget.defaultColumnSpan = defaultColumnSpan;
        widget.defaultRowSpan = defaultRowSpan;
        widget.column = defaultColumn;
        widget.row = defaultRow;
        widget.colspan = defaultColumnSpan;
        widget.columnSpan = defaultColumnSpan;
        widget.rowSpan = defaultRowSpan;
        return widget;
    },

    /**
     * Get new block by type.
     *
     * @param {*} type
     */
    getNewBlockByType(type) {
        return this.getNewWidgetByType(type);
    },

    /**
     * Get widget positioning for new widget.
     *
     * @param {*} id
     * @param {*} widget
     */
    getWidgetPositioningForNewWidget(id, widget = null) {
        const gridColumns = Number(this.grid?.numberOfColumns || 12);
        const defaultColspan = Math.min(
            Math.max(Number(widget?.defaultColumnSpan ?? 12), 1),
            gridColumns
        );
        const defaultRowSpan = Math.max(Number(widget?.defaultRowSpan ?? 1), 1);
        const defaultColumn = Math.min(Math.max(Number(widget?.defaultColumn ?? 1), 1), gridColumns);
        const defaultRow = Math.max(Number(widget?.defaultRow ?? 1), 1);

        return {
            referenceId: id,
            widgetId: null,
            orderIndex: null,
            colspan: defaultColspan,
            offset: defaultColumn - 1,
            row: defaultRow,
            rowSpan: defaultRowSpan,
            hidden: false,
            fullWidth: false,
            alignment: null,
        };
    },

    /**
     * Normalize widget data for storage.
     *
     * @param {*} widget
     */
    normalizeWidgetDataForStorage(widget) {
        const data = this.getWidgetSettingsValues(widget);

        if (widget?.type !== 'cms-dashboard-page-status-check') {
            return data;
        }

        return {
            ...data,
            pageCheckType: String(data?.pageCheckType ?? '404'),
        };
    },
};

export default dashboardWidgetDataProviderWidgets;
